import { safeLoader } from "@/lib/safe-loader";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute } from "@tanstack/react-router";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";
import { Clock, Mail, MapPin, Phone } from "lucide-react";

import { PageHero, Prose } from "../components/site/Page";
import { SchoolMap } from "@/components/site/SchoolMap";
import { ContactForm } from "@/components/site/ContactForm";

export const Route = createFileRoute("/kontakt")({
  loader: async ({ context }) => {
    const data = await safeLoader("sv.kontakt.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined);
    return { seo: resolveSeo(data?.content, "kontakt", "sv") };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:url", content: absoluteUrl("/kontakt") },
        { property: "og:locale", content: "sv_SE" },
        ...ogImage(),
      ],
      links: seoLinks("/kontakt"),
    };
  },
  component: Kontakt,
});

const CONTACTS = [
  { label: "Allmänna frågor", value: "info@sweintschool.com" },
  { label: "Rektor", value: "rektor@sweintschool.com" },
  { label: "Jobb och ansökningar", value: "join@sweintschool.com" },
];

function Kontakt() {
  const { text } = useContent("kontakt");

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      <section className="mx-auto max-w-6xl px-5 py-14">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <MapPin className="size-5 text-primary" />
            <h2 className="mt-3 font-display text-base font-semibold">Besök oss</h2>
            <address className="mt-1 text-sm not-italic leading-relaxed text-foreground/90">
              Urb Costalita 1, Cancelada
              <br />
              29680 Estepona, Spanien
              <br />
              <span className="text-muted-foreground">Mellan Marbella och Estepona</span>
            </address>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <Phone className="size-5 text-primary" />
            <h2 className="mt-3 font-display text-base font-semibold">Ring oss</h2>
            <p className="mt-1 text-sm">
              <a className="hover:text-primary" href="tel:+34607148331">
                +34 607 148 331
              </a>
            </p>
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="size-3.5" /> Telefontid 15:00–16:00, helgfria vardagar
            </p>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <Mail className="size-5 text-primary" />
            <h2 className="mt-3 font-display text-base font-semibold">Mejla oss</h2>
            <dl className="mt-1 space-y-1.5 text-sm">
              {CONTACTS.map((c) => (
                <div key={c.value}>
                  <dt className="text-xs text-muted-foreground">{c.label}</dt>
                  <dd>
                    <a className="hover:text-primary" href={`mailto:${c.value}`}>
                      {c.value}
                    </a>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <InlineCta locale="sv" className="mt-8" />

        <div className="mt-10 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <ContactForm locale="sv" />

          <SchoolMap locale="sv" />
        </div>
      </section>

      <Prose>
        <h2>Kom och hälsa på</h2>
        <p>
          Det bästa sättet att få en känsla för skolan är att komma hit. Vi visar gärna lokaler,
          klassrum och utemiljö och berättar hur en vanlig dag ser ut. Mejla eller ring så hittar vi en
          tid som passar – gärna under skoldagen, så får ni se verksamheten när den är igång.
        </p>

        <h2>Så snabbt svarar vi</h2>
        <p>
          E-post besvaras normalt under helgfria vardagar. Telefontiden är 15:00–16:00, eftersom
          personalen är hos barnen under skoldagen. Under lov och helgdagar kan svarstiden bli längre –
          se{" "}
          <a className="text-primary link-quiet" href="/lasarstider">läsårstiderna</a>.
        </p>

        <h2>Hitta hit</h2>
        <p>
          Skolan ligger i Urb Costalita 1 i Cancelada, mellan Estepona och Marbella, några minuter från
          A-7. Det finns parkering i närheten av skolan. Använd kartan ovan för vägbeskrivning.
        </p>

        <h2>Frågor om ansökan</h2>
        <p>
          Gäller frågan skolstart, platser eller avgifter går det bra att skriva direkt till rektor.
          Vill ni komma igång direkt hittar ni formuläret under{" "}
          <a className="text-primary link-quiet" href="/ansokan">ansökan</a>, och avgifterna på{" "}
          <a className="text-primary link-quiet" href="/avgifter">avgiftssidan</a>.
        </p>
      </Prose>
    </>
  );
}
